/**
 * CosignNotesTable
 *
 * Table for co-sign notes queue. UnifyTable; rows open document review.
 */

import { Anchor, Group, Paper, Text } from '@mantine/core';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UnifyTable, UnifyTableSortAffordance, type UnifyTableColumn } from '@/components/UnifyTable';
import {
  getSupervisionQueueTypeLabel,
  type SupervisionFilters,
  type SupervisionQueueItemWithDocumentId,
} from '../types';
import { SupervisionStatusBadge } from './SupervisionStatusBadge';

type SortColumn = SupervisionFilters['sortBy'];

interface CosignNotesTableProps {
  items: SupervisionQueueItemWithDocumentId[];
  onItemClick: (item: SupervisionQueueItemWithDocumentId) => void;
  sortBy: SortColumn;
  sortDirection: 'asc' | 'desc';
  onSort: (column: SortColumn) => void;
  hideClinicianColumn?: boolean;
}

export function CosignNotesTable({
  items,
  onItemClick,
  sortBy,
  sortDirection,
  onSort,
  hideClinicianColumn = false,
}: CosignNotesTableProps): JSX.Element {
  const formatDate = (d: string | undefined) =>
    d ? new Date(d).toLocaleDateString('en-US') : '—';

  const columns = useMemo((): UnifyTableColumn<SupervisionQueueItemWithDocumentId>[] => {
    const hdr = (label: string, column: SortColumn, onClick: () => void) => {
      const isDateColumn = column === 'oldest' || column === 'newest';
      const isActive = isDateColumn ? sortBy === 'oldest' || sortBy === 'newest' : sortBy === column;
      return (
        <Group
          gap={6}
          wrap="nowrap"
          onClick={onClick}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <Text component="span" size="sm" fw={600}>
            {label}
          </Text>
          <UnifyTableSortAffordance active={isActive} direction={sortDirection} />
        </Group>
      );
    };

    return [
      {
        key: 'client',
        label: 'Client',
        renderHeader: () => hdr('Client', 'patient', () => onSort('patient')),
        render: (item) =>
          item.clientId ? (
            <Anchor component={Link} to={`/dashboard/patients/${item.clientId}`} size="sm" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              {item.clientName ?? '—'}
            </Anchor>
          ) : (
            <Text size="sm">{item.clientName ?? '—'}</Text>
          ),
        getValue: (item) => item.clientName ?? '',
      },
      {
        key: 'episodeOfCare',
        label: 'Episode of Care',
        render: () => <Text size="sm" c="dimmed">Individual Therapy</Text>,
        getValue: () => 'Individual Therapy',
      },
      {
        key: 'sessionDate',
        label: 'Session Date',
        renderHeader: () => hdr('Session Date', 'oldest', () => onSort('oldest')),
        render: (item) => (
          <Text size="sm">
            {item.submittedAt ? formatDate(item.submittedAt) : formatDate(item.dueDate)}
          </Text>
        ),
        getValue: (item) => item.submittedAt ?? item.dueDate ?? '',
      },
      {
        key: 'status',
        label: 'Status',
        render: (item) => <SupervisionStatusBadge status={item.status} size="sm" />,
        getValue: (item) => item.status,
      },
      ...(hideClinicianColumn
        ? []
        : [{
            key: 'provider',
            label: 'Provider',
            renderHeader: () => hdr('Provider', 'clinician', () => onSort('clinician')),
            render: (item: SupervisionQueueItemWithDocumentId) => (
              <Text size="sm">{item.clinicianName}</Text>
            ),
            getValue: (item: SupervisionQueueItemWithDocumentId) => item.clinicianName,
          } as UnifyTableColumn<SupervisionQueueItemWithDocumentId>]),
      {
        key: 'type',
        label: 'Type',
        render: (item) => (
          <Text size="sm">
            {getSupervisionQueueTypeLabel(item)}
          </Text>
        ),
        getValue: (item) => getSupervisionQueueTypeLabel(item),
      },
    ];
  }, [onSort, sortBy, sortDirection, hideClinicianColumn]);

  if (items.length === 0) {
    return (
      <Paper p="lg" withBorder>
        <Text c="gray.6" size="sm" ta="center">
          No notes awaiting co-sign. All items have been reviewed.
        </Text>
      </Paper>
    );
  }

  return (
    <UnifyTable
      withPaper={false}
      columns={columns}
      data={items}
      getRowKey={(item) => item.id}
      onRowClick={onItemClick}
      tableProps={{ style: { width: '100%', tableLayout: 'fixed', minWidth: 800 } }}
    />
  );
}
